import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, NgZone, Output, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CloudApiService, LocationGeoResponse, ResolvedGeo } from '@/cloud/services/cloud-api.service';
import { GoogleMapsLoaderService } from '@/shared/google-maps-loader.service';
import { controlSystemDisplayName } from '@/cloud/services/device-display';

declare const google: any;

/**
 * Dialog for setting a map position: a device's manual override (set `deviceId`) or a location's own position, which the
 * rooms and control systems below it inherit (set `locationId`).
 * Address search geocodes via google.maps.Geocoder (ships in the Maps JS core);
 * the pin is draggable for fine-tuning.
 */
@Component({
    selector: 'app-geo-location-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, DialogModule, ButtonModule, InputTextModule],
    templateUrl: './geo-location-dialog.component.html',
    styleUrl: './geo-location-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GeoLocationDialogComponent {
    private api = inject(CloudApiService);
    private mapsLoader = inject(GoogleMapsLoaderService);
    private ngZone = inject(NgZone);

    @ViewChild('mapContainer') mapContainer?: ElementRef<HTMLDivElement>;

    @Input() deviceId: string | null = null;
    @Input() deviceName: string | null = null;
    /** Set instead of `deviceId` to edit a location's own position. */
    @Input() locationId: string | null = null;
    @Input() locationName: string | null = null;

    get forLocation(): boolean {
        return !!this.locationId;
    }

    targetLabel(): string {
        return this.forLocation ? this.locationName || 'this location' : this.deviceLabel();
    }

    /**
     * Name to show for the device. Callers pass an already-resolved display name, so this only
     * has to keep the dialog from falling back to the raw id when one is missing.
     */
    deviceLabel(): string {
        return controlSystemDisplayName(this.deviceName, this.deviceId);
    }
    /** Currently displayed location (any source) — initial pin position. */
    @Input() initial: ResolvedGeo | null = null;
    /** Whether the target has its own position (shows the Clear button): a device override, or a location's own position. */
    @Input() hasOverride = false;

    @Input() set visible(value: boolean) {
        this._visible.set(value);
        if (value) this.onOpen();
    }
    get visible(): boolean {
        return this._visible();
    }
    @Output() visibleChange = new EventEmitter<boolean>();
    /** Emitted after a successful save or clear so the host can refresh. */
    @Output() locationChanged = new EventEmitter<void>();
    /** Location mode: the location's position after a save or clear. */
    @Output() locationGeoChange = new EventEmitter<LocationGeoResponse>();

    _visible = signal(false);
    searchText = signal('');
    latitude = signal<number | null>(null);
    longitude = signal<number | null>(null);
    address = signal<string | null>(null);
    error = signal<string | null>(null);
    busy = signal(false);
    searching = signal(false);

    private map: any = null;
    private marker: any = null;
    private geocoder: any = null;

    private onOpen(): void {
        this.error.set(null);
        this.busy.set(false);
        this.latitude.set(this.initial?.latitude ?? null);
        this.longitude.set(this.initial?.longitude ?? null);
        this.address.set(this.initial?.address ?? null);
        this.searchText.set(this.initial?.address ?? '');
    }

    /** Called by p-dialog (onShow) — the map container only exists once the dialog is rendered. */
    async initMap(): Promise<void> {
        try {
            await this.mapsLoader.load();
        } catch (err: any) {
            this.error.set(err?.message || 'Failed to load Google Maps');
            return;
        }

        if (!this.mapContainer?.nativeElement) return;

        const hasInitial = this.latitude() !== null && this.longitude() !== null;
        const center = hasInitial
            ? { lat: this.latitude()!, lng: this.longitude()! }
            : { lat: 30, lng: 0 };

        this.map = new google.maps.Map(this.mapContainer.nativeElement, {
            center,
            zoom: hasInitial ? 14 : 2,
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: 'greedy',
            controlSize: 28
        });
        this.geocoder = new google.maps.Geocoder();

        this.marker = new google.maps.Marker({
            position: hasInitial ? center : null,
            map: this.map,
            draggable: true
        });
        this.marker.addListener('dragend', () => {
            const pos = this.marker.getPosition();
            if (!pos) return;
            this.ngZone.run(() => this.setCoords(pos.lat(), pos.lng(), null, true));
        });
        this.map.addListener('click', (e: any) => {
            this.ngZone.run(() => this.setCoords(e.latLng.lat(), e.latLng.lng(), null, true));
        });
    }

    private setCoords(lat: number, lng: number, address: string | null, reverseGeocode: boolean): void {
        this.latitude.set(Math.round(lat * 1e6) / 1e6);
        this.longitude.set(Math.round(lng * 1e6) / 1e6);
        this.address.set(address);
        this.error.set(null);
        if (this.marker) this.marker.setPosition({ lat, lng });

        if (reverseGeocode && this.geocoder) {
            this.geocoder.geocode({ location: { lat, lng } }, (results: any[], status: string) => {
                this.ngZone.run(() => {
                    if (status === 'OK' && results?.length) {
                        this.address.set(results[0].formatted_address);
                        this.searchText.set(results[0].formatted_address);
                    }
                    // On failure keep the raw coordinates — the address is cosmetic
                });
            });
        }
    }

    search(): void {
        const query = this.searchText().trim();
        if (!query || !this.geocoder) return;
        this.searching.set(true);
        this.error.set(null);
        this.geocoder.geocode({ address: query }, (results: any[], status: string) => {
            this.ngZone.run(() => {
                this.searching.set(false);
                if (status === 'OK' && results?.length) {
                    const result = results[0];
                    const location = result.geometry.location;
                    this.setCoords(location.lat(), location.lng(), result.formatted_address, false);
                    this.searchText.set(result.formatted_address);
                    if (this.map) {
                        this.map.panTo(location);
                        this.map.setZoom(14);
                    }
                } else if (status === 'ZERO_RESULTS') {
                    this.error.set('No results found for that address');
                } else if (status === 'REQUEST_DENIED') {
                    this.error.set('Geocoding is not enabled for this Google Maps API key');
                } else {
                    this.error.set(`Address lookup failed (${status})`);
                }
            });
        });
    }

    /** Manual lat/lng edits from the numeric inputs. */
    onCoordInput(): void {
        const lat = this.latitude();
        const lng = this.longitude();
        if (lat === null || lng === null || isNaN(lat) || isNaN(lng)) return;
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return;
        this.address.set(null);
        if (this.marker && this.map) {
            this.marker.setPosition({ lat, lng });
            this.map.panTo({ lat, lng });
        }
    }

    canSave(): boolean {
        const lat = this.latitude();
        const lng = this.longitude();
        return lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)
            && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && !this.busy();
    }

    async save(): Promise<void> {
        if ((!this.deviceId && !this.locationId) || !this.canSave()) return;
        this.busy.set(true);
        this.error.set(null);
        try {
            if (this.locationId) {
                this.locationGeoChange.emit(await this.api.setLocationGeo(this.locationId, this.latitude()!, this.longitude()!, this.address()));
            } else {
                await this.api.updateDeviceGeo(this.deviceId!, this.latitude()!, this.longitude()!, this.address());
                await this.refreshDevice();
            }
            this.locationChanged.emit();
            this.close();
        } catch (err: any) {
            this.error.set(err?.error || err?.message || 'Failed to save location');
        } finally {
            this.busy.set(false);
        }
    }

    async clearOverride(): Promise<void> {
        if (!this.deviceId && !this.locationId) return;
        this.busy.set(true);
        this.error.set(null);
        try {
            if (this.locationId) {
                this.locationGeoChange.emit(await this.api.clearLocationGeo(this.locationId));
            } else {
                await this.api.clearDeviceGeo(this.deviceId!);
                await this.refreshDevice();
            }
            this.locationChanged.emit();
            this.close();
        } catch (err: any) {
            this.error.set(err?.error || err?.message || 'Failed to clear location override');
        } finally {
            this.busy.set(false);
        }
    }

    private async refreshDevice(): Promise<void> {
        if (!this.deviceId) return;
        const device = await this.api.getDevice(this.deviceId);
        if (device) this.api.seedDevice(device);
    }

    close(): void {
        this._visible.set(false);
        this.visibleChange.emit(false);
        this.map = null;
        this.marker = null;
        this.geocoder = null;
    }
}
